import React, { useMemo } from 'react';
import i18n from '../../../../i18n';
import { euroFormatter } from '../../../../functions/formatters';
import QuantityStepper from '../../../../components/QuantityStepper/QuantityStepper';
import Icon from '../../../../icons/Icon';

const optionQuantity = (option) => {
    const qty = Number(option?.quantity);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
};

export const getOptionDisplayName = (option) => (
    option?.product_object?.name
    || option?.product_object?.internal_name
    || option?.name
    || null
);

const ProductOptionCard = ({ option, value, onChange }) => {
    const selectionType = option?.selection_type || 'quantity';
    const isRequired = Boolean(option?.is_required);
    const isYesNo = selectionType === 'yes_no';
    const configuredQty = optionQuantity(option);

    const optionName = getOptionDisplayName(option);
    const optionPrice = Number(option?.price ?? option?.product_object?.price ?? 0) || 0;
    const optionDiscountPrice = Number(
        option?.price_after_discount
        ?? option?.price
        ?? option?.product_object?.price_after_discount
        ?? option?.product_object?.price
        ?? 0
    ) || 0;

    const isSelected = isRequired || (
        isYesNo
            ? value === true || (typeof value === 'number' && value > 0)
            : (value || 0) > 0
    );

    const priceDisplay = useMemo(() => {
        const hasDiscount = optionDiscountPrice != optionPrice;
        return (
            <div className="d-flex align-items-baseline gap-2">
                <div className="fw-semibold">{euroFormatter(optionDiscountPrice)}</div>
                {hasDiscount && (
                    <div className="text-muted text-decoration-line-through small">
                        {euroFormatter(optionPrice)}
                    </div>
                )}
            </div>
        );
    }, [optionDiscountPrice, optionPrice]);

    return (
        <div
            className={`product-option-card card h-100 ${isSelected ? 'border-primary' : ''}`}
            style={{
                transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                maxWidth: '320px',
                wordBreak: 'break-word',
                borderRadius: '0.9rem',
            }}
        >
            <div className="card-body p-3 d-flex flex-column gap-2">
                <div className="d-flex justify-content-between align-items-start gap-3">
                    <div style={{ minWidth: 0 }}>
                        {optionName && (
                            <div className="fw-semibold" style={{ lineHeight: 1.2 }}>
                                {optionName}
                            </div>
                        )}
                        <div className={optionName ? 'mt-1' : ''}>{priceDisplay}</div>
                    </div>

                    {isYesNo && !isRequired && (
                        <div className="form-check m-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id={`option-${option.id}`}
                                checked={isSelected}
                                aria-label={i18n.t('Select option')}
                                onChange={(e) => onChange?.(e.target.checked)}
                            />
                        </div>
                    )}
                </div>

                {(selectionType === 'quantity' || isRequired) && (
                    <div className="mt-2 d-flex align-items-center justify-content-between gap-2">
                        <div className="text-muted small">
                            {i18n.t('Quantity')}
                        </div>
                        {isRequired ? (
                            <div className="fw-semibold">{configuredQty}</div>
                        ) : (
                            <QuantityStepper
                                value={value || 0}
                                min={0}
                                inputAriaLabel={i18n.t('Quantity')}
                                decrementAriaLabel={i18n.t('Decrease quantity')}
                                incrementAriaLabel={i18n.t('Increase quantity')}
                                minusIcon={<Icon name="ICON_MINUS" />}
                                plusIcon={<Icon name="ICON_PLUS" />}
                                inputProps={{
                                    className: 'form-control-sm product-option-qty-stepper-input',
                                }}
                                onChange={(nextQty) => onChange?.(nextQty)}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductOptionCard;
